import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class QueryParserService {

  parseFieldQuery(queryField: string, query: string, returnWildcardString: boolean = false) {
    if (queryField === 'content') {
      // For content searches, do not modify the query except for adding wildcards if needed
      return this.parse(query, returnWildcardString)
    }
    let parsedQuery: string;
    if (queryField?.endsWith('upper')) {
      // Only change to upper case for upper case fields!!
      parsedQuery = this.parse(query, true, ' ', '_', returnWildcardString).toLocaleUpperCase();
    } else {
      parsedQuery = this.parse(query, true, '', '', returnWildcardString)
    }
    return parsedQuery;
  }

  parse(query: string, addWildCards: boolean, replaceFromString?: string, replaceWithString?: string, returnWildcardString: boolean = false) {
    let res = '';

    query = this.removeIllegalText(query);
    addWildCards = this.getAddWildCards(query, addWildCards);
    if (query) {
      if (returnWildcardString) {
        return query.split(' ').map(q => `*${q}*`).join('-_-');
      }

      const qTrim = query.trim();
      if (qTrim.startsWith('"') && qTrim.endsWith('"')) {
        if (qTrim === '"') {
          return '';
        }
        return qTrim.substring(1, qTrim.length - 1);
      }
      const hasQuotes = ['"', '\''].includes(qTrim.substring(qTrim.length - 1));
      res = this.splitAndCombineQuery(query, hasQuotes, addWildCards);
      if (replaceFromString && replaceWithString) {
        res = res.replaceAll(replaceFromString, replaceWithString)
      }
      res = res.trim();
      if (addWildCards && res.split(' ').length === 1 && !hasQuotes) {
        res = '*' + res;
      }
    } else if (addWildCards) {
      res = '*:*';
    }
    return res;
  }

  private getAddWildCards(query: string, addWildCards: boolean = true) {
    if (addWildCards && query?.includes('~') || query?.includes('^')) {
      addWildCards = false;
    }
    return addWildCards;
  }

  private splitAndCombineQuery(query: string, hasQuotes: boolean, addWildCards: boolean) {
    let res = '';
    let spc = '';
    const qSplit = this.cleanSplit(query);
    if (qSplit.length > 0) {
      for (let t = 0; t < qSplit.length - 1; t++) {
        res += spc + qSplit[t];
        spc = ' ';
      }
      const last = this.clean(qSplit.at(-1));
      res += spc + last;
      res = res.trim();
      if (!hasQuotes && addWildCards) {
        res += '*';
      }
    }
    return res;
  }

  private cleanSplit(query: string) {
    return query.split(/[\s/()\-+:«»;,.&"]/g).filter(q => q !== '' && q !== ' ');
  }

  private removeIllegalText(query: string) {
    const requireStarts = ['^','AND', 'OR'];
    const requireEnds = ['^', 'OR', 'AND', 'NOT'];
    query = query ? query.trim() : '';
    if (!query) {
      return query;
    }
    for (const requireStart of requireStarts) {
      if (query.startsWith(requireStart)) {
        query = query.substring(requireStart.length).trim();
      }
    }
    for (const requireEnd of requireEnds) {
      if (query.endsWith(requireEnd)) {
        query = query.substring(0, query.length - requireEnd.length).trim();
      }
    }
    const indexOfHat = query.indexOf('^');
    if (indexOfHat !== -1) {
      const nextChar = query.charAt(indexOfHat + 1);
      // Must remove the ^ character if next character is not number, else search will fail
      const nextCharNumber = Number(nextChar);
      if (Number.isNaN(nextCharNumber)) {
        return query.replace('^', '');
      }
    }
    return query;
  }

  private clean(query: string) {
    return query.replaceAll(/[[\]{}]/g, '').trim();
  }

  /**
   * Converts a string to a compact upper-case Solr-safe token (ADR-0001).
   * Must match Python utils.index_utils.to_compact_upper: Unicode letters/digits
   * are uppercased; every other code point is UTF-8-encoded as _XX per byte.
   */
  toCompactUpper(val: string | null | undefined): string | null | undefined {
    if (val === null || val === undefined || val === '') {
      return val;
    }
    let res = '';
    for (const char of val.trim()) {
      if (/^\p{L}$/u.test(char) || /^\p{N}$/u.test(char)) {
        res += char.toUpperCase();
      } else {
        const bytes = new TextEncoder().encode(char);
        for (const byte of bytes) {
          res += '_' + byte.toString(16).toUpperCase().padStart(2, '0');
        }
      }
    }
    return res;
  }

  /**
   * Lucene regex body for a raw substring at encoded character boundaries.
   * Empty input returns null: the gap alone would match every term.
   */
  compactUpperContainsPattern(value: string | null | undefined): string | null {
    if (value == null) {
      return null;
    }
    return this.compactUpperEncodedContainsPattern(this.toCompactUpper(value));
  }

  /** Same boundary regex when the caller already holds a compact-upper token. */
  compactUpperEncodedContainsPattern(encoded: string | null | undefined): string | null {
    if (!encoded) {
      return null;
    }
    const gap = '([^_]|_[0-9A-F]{2})*';
    return `${gap}${encoded}${gap}`;
  }

  /** Solr clause `field:/pattern/`. A boost sits outside the slashes. */
  compactUpperEncodedContainsClause(
    field: string,
    encoded: string | null | undefined,
    boost?: number
  ): string | null {
    const pattern = this.compactUpperEncodedContainsPattern(encoded);
    if (!pattern) {
      return null;
    }
    const clause = `${field}:/${pattern}/`;
    return boost == null ? clause : `(${clause})^${boost}`;
  }

  /**
   * Build a scored Lucene query for concept/place/location keyword search (P10-1446).
   * Exact > prefix > contains on the name field; optional code and indexed text at lower boosts.
   * When querying artifact_name_upper, also boost name.name_upper so bare concept names
   * (e.g. Centimeter) outrank code-prefixed compounds (cm2: Kvadratcentimeter).
   */
  buildRelevanceQuery(
    queryField: string,
    query: string,
    options?: { contentField?: string; includeCode?: boolean }
  ): string | null {
    const term = this.relevanceTerm(queryField, query);
    if (!term) {
      return null;
    }
    const clauses = [
      ...this.nameBoostClauses(queryField, term),
    ];
    if (queryField === 'artifact_name_upper') {
      clauses.push(...this.nameBoostClauses('name.name_upper', term));
    }
    if (options?.includeCode) {
      // Only boost code for single-token (code-shaped) queries; multi-word terms break Lucene field syntax.
      const codeTerm = this.parse(query, false);
      if (codeTerm && !/\s/.test(codeTerm)) {
        clauses.push(...this.nameBoostClauses('code', codeTerm));
      }
    }
    if (options?.contentField) {
      const contentTerm = this.parse(query, true);
      if (contentTerm && contentTerm !== '*:*') {
        clauses.push(`${this.indexedCatchAllField(options.contentField)}:(${contentTerm})^1`);
      }
    }
    return `(${clauses.join(' OR ')})`;
  }

  /**
   * Object-search view relevance (P10-1147): exact > prefix > contains on each name field,
   * plus optional catch-all text field at ^1.
   * One word uses the analyzed name field. Several words use the compact-upper sibling
   * (`agent_name.name` → `agent_name.name_upper`) so the whole phrase is one token,
   * and also require each word somewhere in that name at a lower boost.
   */
  buildViewRelevanceQuery(
    nameFields: string[],
    query: string,
    textField?: string
  ): string | null {
    if (!nameFields?.length || !query?.trim()) {
      return null;
    }
    const term = this.parse(query, false).trim().toLocaleLowerCase();
    if (!term || term === '*:*') {
      return null;
    }
    if (/\s/.test(term)) {
      return this.multiWordViewRelevanceQuery(nameFields, query, textField);
    }
    const clauses = nameFields.flatMap(field => this.nameBoostClauses(field, term));
    this.appendViewTextCatchAll(clauses, query, textField);
    return `(${clauses.join(' OR ')})`;
  }

  private multiWordViewRelevanceQuery(
    nameFields: string[],
    query: string,
    textField?: string
  ): string | null {
    const phrase = this.toCompactUpper(this.unquote(query));
    if (!phrase) {
      return null;
    }
    const words = this.compactUpperWords(query);
    const clauses = nameFields.flatMap(field => {
      const upperField = field.endsWith('_upper') ? field : `${field}_upper`;
      const fieldClauses = this.nameBoostClauses(upperField, phrase);
      if (words.length > 1) {
        const contains = words
          .map(word => this.compactUpperEncodedContainsClause(upperField, word))
          .filter((clause): clause is string => !!clause)
          .join(' AND ');
        if (contains) {
          fieldClauses.push(`(${contains})^20`);
        }
      }
      return fieldClauses;
    });
    this.appendViewTextCatchAll(clauses, query, textField);
    return `(${clauses.join(' OR ')})`;
  }

  private appendViewTextCatchAll(clauses: string[], query: string, textField?: string): void {
    if (!textField) {
      return;
    }
    const textTerm = this.parse(query, true);
    if (textTerm && textTerm !== '*:*') {
      clauses.push(`${textField}:(${textTerm.toLocaleLowerCase()})^1`);
    }
  }

  private compactUpperWords(query: string): string[] {
    return this.unquote(query)
      .split(/\s+/)
      .map(word => this.toCompactUpper(word) || '')
      .filter(word => word !== '');
  }

  private unquote(query: string): string {
    const trimmed = query.trim();
    if (trimmed.length > 1 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return trimmed.slice(1, -1);
    }
    return trimmed;
  }

  private nameBoostClauses(field: string, term: string): string[] {
    const encoded = field.toLocaleLowerCase().includes('_upper');
    const contains = encoded
      ? this.compactUpperEncodedContainsClause(field, term, 5)
      : `${field}:*${term}*^5`;
    return [
      `${field}:${term}^100`,
      `${field}:${term}*^40`,
      contains ?? `${field}:*${term}*^5`,
    ];
  }

  private relevanceTerm(queryField: string, query: string): string {
    if (!query?.trim()) {
      return '';
    }
    if (queryField?.toLocaleLowerCase().includes('_upper') || queryField?.endsWith('upper')) {
      const trimmedQuery = query.trim();
      const unquotedQuery = trimmedQuery.startsWith('"') && trimmedQuery.endsWith('"')
        ? trimmedQuery.slice(1, -1)
        : trimmedQuery;
      return this.toCompactUpper(unquotedQuery) || '';
    }
    return this.parse(query, false).trim();
  }

  /** `content` is stored-only; keyword recall must use indexed `text`. */
  private indexedCatchAllField(field: string): string {
    return field === 'content' ? 'text' : field;
  }


}
